<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import InputClearButton from '@/components/ui/InputClearButton.vue';
import type { IdSearch } from '@/components/ui/IdSearch';

// Given several searches, the user picks one by a toggle beside the input.
// The one whose ID is in the URL is selected, else the first; `selected`
// carries the choice back to the page.
const props = defineProps<{ searches: IdSearch[] }>();
const selected = defineModel<string>('selected');

const route = useRoute();
const router = useRouter();

function readId(search: IdSearch): string {
  const value = search.read(route);
  return typeof value === 'string' ? value : '';
}

const inRoute = computed(() => props.searches.find((search) => readId(search)));
watch(
  () => (inRoute.value ?? props.searches[0])?.name,
  (name) => (selected.value = name),
  { immediate: true }
);

const search = computed(
  () => props.searches.find((option) => option.name === selected.value) ?? props.searches[0]
);
// A searched ID's own page keeps its heading whichever search is selected.
const heading = computed(() => inRoute.value ?? search.value);
const current = computed(() => readId(search.value));
const input = ref(current.value);
watch(current, (value) => (input.value = value));

function submit() {
  const value = input.value.trim();
  if (value) {
    router.push(search.value.to(value));
  }
}

function clear() {
  input.value = '';
  if (current.value) {
    router.push(search.value.clearPath);
  }
}
</script>

<template>
  <h1 class="text-2xl font-bold text-gray-900">{{ heading.title }}</h1>
  <p class="mt-1 text-sm text-gray-500">{{ heading.subtitle }}</p>
  <form
    class="mt-4 mb-12 flex max-w-xl"
    :data-test="`id-search-${search.name}`"
    @submit.prevent="submit"
  >
    <div
      v-if="searches.length > 1"
      role="group"
      aria-label="Search by"
      class="flex shrink-0 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 p-0.5"
      data-test="id-search-by"
    >
      <button
        v-for="option in searches"
        :key="option.name"
        type="button"
        :aria-pressed="option.name === search.name"
        :class="[
          'rounded-md px-2.5 text-xs font-medium whitespace-nowrap',
          option.name === search.name
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-500 hover:text-gray-700',
        ]"
        :data-test="`id-search-by-${option.name}`"
        @click="selected = option.name"
      >
        {{ option.label }}
      </button>
    </div>
    <div class="relative min-w-0 flex-1">
      <MagnifyingGlassIcon
        class="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400"
        aria-hidden="true"
      />
      <input
        v-model="input"
        type="text"
        :placeholder="search.label"
        :aria-label="search.label"
        :data-test="`id-search-${search.name}-input`"
        :class="[
          'w-full border border-gray-300 bg-white py-2 pr-9 pl-9 font-mono text-sm text-gray-700',
          searches.length > 1 ? 'rounded-r-lg' : 'rounded-lg',
        ]"
      />
      <InputClearButton v-if="input" :label="`Clear ${search.label}`" @click="clear" />
    </div>
  </form>
</template>
